/**
 * api.ts — Centre de notifications (#12+13 P1.1).
 *   GET  /api/v1/notifications              — notification.view
 *   GET  /api/v1/notifications/unread-count — notification.view
 *   POST /api/v1/notifications/:id/read | /unread
 */
import { apiClient } from '@/lib/apiClient'
import type { PatternType, Sens } from '@/modules/phenomena/types/phenomena.types'

/**
 * Le payload d'une notification PHENOMENA. ⚠ Il porte les CHAMPS, pas la
 * phrase : l'écran la compose avec `composerPhrase`, le vocabulaire n'existant
 * qu'à un seul endroit (chantier #11).
 */
/** ⚠ Les mêmes valeurs que `Phenomenon.scale` du module — jamais un doublon élargi. */
type Scale = 'PROPORTION_0_1' | 'SCORE_0_100' | 'COUNT' | 'MONETARY_USD' | 'RATIO_LARGE'

export interface PayloadPhenomene {
  phenomenonId:    string
  kpi:             string
  patternType:     PatternType
  ampleur:         string
  fiabilite:       string
  sens:            Sens
  nature:          string
  channel:         string
  deltaPct:        number | null
  /**
   * ⚠ LES CINQ ENTRÉES DU DELTA NATIF (`D-P1-AFFICHAGE-DELTA-NATIF-V1`, V4.25).
   *   OPTIONNELLES : les lignes émises AVANT ce palier ne les portent pas — elles
   *   se replient sur le rendu actuel, et aucune migration de payload n'est faite.
   * ⚠ Le delta se RECALCULE depuis `currentValue − referenceValue`, jamais depuis
   *   `deltaPct` — c'est la décision gravée, pas un choix de ce palier.
   */
  currentValue?:   number | null
  referenceValue?: number | null
  scale?:          Scale
  suffix?:         string
  magnitude?:      number | null
  direction:       string
  scopeBrandCode:  string | null
  detail:          { length_weeks: number | null; window_weeks: number | null }
  /**
   * ⚠ LE BADGE « +N ». Nombre de patterns ayant détecté LE MÊME fait métier
   *   (#329). `1` = un seul, pas de badge. Ce n'est pas un compteur de
   *   nouvelles : c'est un indice de concordance.
   */
  groupSize:       number
  groupSignatures: string[]
  scopeHash:       string
  groupId:         string | null
  drillable:       boolean
}

/**
 * Le payload d'une notification SFM_PERF (#12+13 P1.3). ⚠ MÊME PRINCIPE QUE
 * PHENOMENA : des champs, pas une phrase. `merchCode` est un CODE — le moteur
 * d'alertes ne porte aucun nom de personne (#065), et l'écran n'en invente pas.
 */
export interface PayloadSfmPerf {
  merchCode: string
  alertCode: string
  level:     string
  channel:   string
  value:     number | null
  coverage:  number | null
  /**
   * ⚠ Δ BRUT vs LA SEMAINE PRÉCÉDENTE PRÉSENTE EN BASE, jamais pré-formaté.
   *   ABSENT s'il n'y a pas de mémoire antérieure — jamais `null`, jamais 0 :
   *   un zéro se lirait comme une mesure (#449).
   */
  delta?:    number
  /**
   * ⚠ LA DIRECTION DU SEUIL, RECOPIÉE DU MODULE — c'est elle qui donne le SENS
   *   d'un Δ. Absente si le seuil ne la porte pas : le Δ reste alors neutre.
   */
  direction?: string
  note?:     string
}

/**
 * Le payload du DIGEST Data Accuracy (#12+13 P1.4). ⚠ PREMIER OBJET DU CENTRE
 * QUI DONNE UN ÉTAT au lieu d'annoncer un événement — d'où la sévérité `null`
 * et l'absence de seuil : un bulletin paraît chaque semaine, bon ou mauvais.
 * ⚠ `taux` et `delta` sont en POINTS (62.3 = 62,3 %), `null` = population vide
 *   ou première semaine suivie — jamais un zéro de complaisance.
 */
export interface PayloadDataAccuracy {
  /**
   * ⚠ DEUX VOLETS, DEUX DÉNOMINATEURS — ceux de la PAGE Data Accuracy, importés :
   *   preuves = `proven / visits` · qualité = `quality_pass / quality_evaluable`.
   *   Le centre doit dire LE MÊME chiffre que la page, sinon aucun des deux
   *   écrans n'est croyable.
   */
  tauxQualite:  number | null
  deltaQualite: number | null
  tauxPreuves:  number | null
  deltaPreuves: number | null

  visits:     number
  proven:     number
  evaluables: number
  pass:       number

  semainePrecedente:    string | null
  tauxQualitePrecedent: number | null
  tauxPreuvesPrecedent: number | null

  /** Le détail du drill P1.6 — des CODES merch, jamais des noms (#065). */
  parMerch: Array<{
    code:         string
    visits:       number
    proven:       number
    evaluables:   number
    pass:         number
    tauxQualite:  number | null
    tauxPreuves:  number | null
    deltaQualite: number | null
    deltaPreuves: number | null
  }>
}

/**
 * Le payload d'une notification PI-CI (#12+13 P1.5). ⚠ Des CHAMPS, pas une
 * phrase — l'écran compose (`titresPici.ts`).
 * ⚠ `nature` sépare la NOUVEAUTÉ de la vague initiale et du RETOUR d'archive :
 *   `skus` et `archived` étant disjoints à un run donné, seule la mémoire de ce
 *   qui a été annoncé peut les distinguer, et elle est calculée côté serveur.
 */
export interface PayloadPiCi {
  nature:        'NOUVELLE' | 'EN_COURS' | 'RETOUR'
  skuId:         string
  skuName:       string
  modelName:     string | null
  brandName:     string
  /** ⚠ Lu en base (`brands.is_client_brand`), jamais deviné d'un nom. */
  isClientBrand: boolean
  channel:       string
  startWeek:     string
  weeksSince:    number
  firstCircuit:  'OFFICIEL' | 'PI-CI' | null
  /** Les deux circuits ont été vus dès la première semaine de données. */
  startIsWindowEdge: boolean
  volumeOfficiel: number | null
  volumeCi:       number | null
  /**
   * ⚠ Δ BRUTS vs la mémoire précédente PRÉSENTE en base. ABSENTS s'il n'y en a
   *   pas — jamais `null`, jamais 0 : un zéro se lirait comme une mesure (#449).
   */
  deltaOfficiel?: number
  deltaCi?:       number
}

export interface NotificationDto {
  id:        string
  source:    string
  kind:      string
  severity:  string | null
  audience:  string
  weekIso:   string
  /**
   * ⚠ Identifie la notification DANS SA SOURCE. MX RM Flow rend TROIS lignes
   *   sous la meme source : seule cette cle les distingue, et son ordinal porte
   *   aussi l ordre d affichage voulu.
   */
  itemKey:   string
  payload:   Record<string, unknown>
  createdAt: string
  read:      boolean
  readAt:    string | null
  /**
   * ⚠ La date de l'IMPORT qui a produit la notification. `null` pour une
   *   `USER_ACTION` (publication Flash Info) — elle porte sa date dans son
   *   payload, et l'écran compose.
   */
  dateImport: string | null
}

export interface NotificationsResponse {
  data: NotificationDto[]
  meta: { total: number; page: number; pageSize: number; unread: number }
}

export async function fetchNotifications(params?: {
  page?: number
  pageSize?: number
}): Promise<NotificationsResponse> {
  const q = new URLSearchParams()
  if (params?.page)     q.set('page',      String(params.page))
  if (params?.pageSize) q.set('page_size', String(params.pageSize))
  const qs = q.toString()
  const res = await apiClient.get<NotificationsResponse>(`/notifications${qs ? `?${qs}` : ''}`)
  return res.data
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await apiClient.get<{ data: { unread: number } }>('/notifications/unread-count')
  return res.data.data.unread
}

export async function setNotificationRead(id: string, read: boolean): Promise<void> {
  await apiClient.post(`/notifications/${id}/${read ? 'read' : 'unread'}`)
}
